import { computed } from 'vue';
import { setCurrentActiveEl } from '@/core/directive/enterFocusTracker';
import { ref } from 'vue';
import { AuthService } from '@/service/AuthService';
import { loadingState } from '../loader/loading';
import axios from 'axios';
import { API_RPT_URL } from './constants';

export const removeCommas = (val) => {
    if (!val) return 0;
    return parseFloat(val.toString().replace(/,/g, '')) || 0;
};

export const getCurrentDate = () => {
    const d = new Date();
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
};

export const sanitizeFieldsForSave = (obj, options = {}) => {
    const { numericFields = [], dateFields = [] } = options;
    const cleaned = JSON.parse(JSON.stringify(obj)); // Deep clone to unproxy

    numericFields.forEach((key) => {
        if (cleaned[key]) {
            cleaned[key] = parseFloat(cleaned[key].toString().replace(/,/g, '')) || 0;
        }
    });

    dateFields.forEach((key) => {
        if (cleaned[key]) {
            cleaned[key] = convertToDbDate(cleaned[key]);
        }
    });

    return cleaned;
};

export const formatFieldsForDisplay = (obj, options = {}) => {
    const { numericFields = [], dateFields = [], dateTimeFields = [] } = options;
    const formatted = { ...obj };

    numericFields.forEach((key) => {
        if (formatted[key] !== undefined && formatted[key] !== null && formatted[key] !== '') {
            formatted[key] = formatNumber(formatted[key]);
        } else {
            formatted[key] = 0;
        }
    });

    dateFields.forEach((key) => {
        if (formatted[key]) {
            formatted[key] = convertToDisplayDate(formatted[key]);
        }
    });

    dateTimeFields.forEach((key) => {
        if (formatted[key]) {
            formatted[key] = convertToDisplayDateTime(formatted[key]);
        }
    });

    return formatted;
};

export const convertToDisplayDate = (dateStr) => {
    if (!dateStr) return '';

    // Extract only date portion if datetime is present
    const cleanDate = dateStr.split(' ')[0]; // "2025-10-15 00:00:00.000" → "2025-10-15"

    const parts = cleanDate.split('-');
    if (parts.length !== 3) return dateStr;

    const [y, m, d] = parts;
    return `${d.padStart(2, '0')}/${m.padStart(2, '0')}/${y}`;
};
export const convertToDisplayDateTime = (dateTimeStr) => {
    if (!dateTimeStr) return '';

    const [datePart, timePartRaw] = dateTimeStr.split(' ');
    if (!datePart) return dateTimeStr;

    const parts = datePart.split('-');
    if (parts.length !== 3) return dateTimeStr;

    const [y, m, d] = parts;
    const displayDate = `${d.padStart(2, '0')}/${m.padStart(2, '0')}/${y}`;

    if (!timePartRaw) return displayDate;

    // Remove milliseconds if present
    const timePart = timePartRaw.split('.')[0];
    let [hour, minute] = timePart.split(':'); // ignore seconds now

    hour = parseInt(hour, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    hour = hour % 12;
    if (hour === 0) hour = 12;

    const displayTime = `${String(hour).padStart(2, '0')}:${minute} ${ampm}`;

    return `${displayDate} ${displayTime}`;
};

export const convertToDbDate = (dateStr) => {
    if (!dateStr) return '';
    const parts = dateStr.split('/');
    if (parts.length !== 3) return dateStr;
    const [d, m, y] = parts;
    return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
};

export const convertDatePickerToUIDateFormat = (input, format = 'dd/mm/yy') => {
    if (!input) return '';

    let date;

    // If it's already a Date object
    if (input instanceof Date) {
        date = input;
    }

    // If it's a string like '2025-07-01 00:00:00.000' or '2025-07-01'
    else if (typeof input === 'string') {
        const [datePart] = input.split(' '); // Strip time part if exists
        const parts = datePart.split('-');
        if (parts.length === 3) {
            const [year, month, day] = parts;
            date = new Date(Number(year), Number(month) - 1, Number(day));
        } else {
            return '';
        }
    }

    // If still invalid
    if (!(date instanceof Date) || isNaN(date.getTime())) return '';

    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();

    switch (format) {
        case 'dd/mm/yy':
            return `${day}/${month}/${String(year).slice(-2)}`;
        case 'dd/mm/yyyy':
            return `${day}/${month}/${year}`;
        case 'yyyy':
            return `${year}`;
        case 'yyyy-mm-dd':
            return `${year}-${month}-${day}`;
        default:
            return date.toLocaleDateString();
    }
};

export function convertDbDateToDatePickerFormat(str) {
    if (!str) return null;
    const [datePart] = str.split(' ');
    const [year, month, day] = datePart.split('-').map(Number);
    return new Date(year, month - 1, day);
}

// export const formatNumber = (value) => {
//     if (value == null || value === '') return '0';

//     const num = parseFloat(value.toString().replace(/,/g, ''));
//     if (isNaN(num)) return '';

//     const hasDecimal = !Number.isInteger(num);

//     const [int, dec] = num.toString().split('.');
//     const formattedInt = int.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

//     if (!hasDecimal || dec === '0') {
//         return formattedInt; // return without ".0"
//     }

//     return `${formattedInt}.${dec}`;
// };

export const formatNumber = (value) => {
    if (value == null || value === '') return '0';

    const num = parseFloat(value.toString().replace(/,/g, ''));
    if (isNaN(num)) return '';

    // round to 3 decimals
    const rounded = Math.round(num * 1000) / 1000;

    const hasDecimal = !Number.isInteger(rounded);

    const [int, dec] = rounded.toString().split('.');
    const formattedInt = int.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

    if (!hasDecimal || dec === '0') {
        return formattedInt; // return without ".0"
    }

    return `${formattedInt}.${dec}`;
};

// export const formatNumber = (value) => {
//     if (value === undefined || value === null || value === '' || !isFinite(value)) return '0';

//     const num = Number(value.toString().replace(/,/g, ''));
//     if (isNaN(num)) return '0';

//     // round to 5 decimals
//     const rounded = Math.round(num * 100000) / 100000;

//     const [int, dec] = rounded.toFixed(5).split('.');
//     const formattedInt = int.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

//     // remove trailing zeros from decimals
//     const trimmedDec = dec.replace(/0+$/, '');

//     // hide decimal part completely if all zeros
//     if (trimmedDec === '') {
//         return formattedInt;
//     }

//     return `${formattedInt}.${trimmedDec}`;
// };

export const sumField = (formRowDetail, field) => {
    return computed(() => {
        const sum = formRowDetail.reduce((total, item) => {
            const raw = removeCommas(item[field]);
            return total + (parseFloat(raw) || 0);
        }, 0);
        return formatNumber(sum);
    });
};

export function checkProductAlreadyExist(formRowDetail, formRow, toast) {
    const alreadyExists = formRowDetail.some((existingRow) => existingRow.product_id === formRow.product_id);
    if (alreadyExists) {
        toast.add({
            severity: 'warn',
            summary: 'Already Exist',
            detail: 'Product already exist',
            life: 3000
        });
        return true;
    }

    return false;
}
export function checkAccountAlreadyExist(formRowDetail, formRow, toast) {
    const alreadyExists = formRowDetail.some((existingRow) => existingRow.account_id === formRow.account_id);
    if (alreadyExists) {
        toast.add({
            severity: 'warn',
            summary: 'Already Exist',
            detail: 'Account already exist',
            life: 3000
        });
        return true;
    }

    return false;
}
export function checkPartyAlreadyExist(formRowDetail, formRow, toast) {
    const alreadyExists = formRowDetail.some((existingRow) => existingRow.party_id === formRow.party_id);
    if (alreadyExists) {
        toast.add({
            severity: 'warn',
            summary: 'Already Exist',
            detail: 'Party already exist',
            life: 3000
        });
        return true;
    }

    return false;
}
export function checkBranchAlreadyExist(formRowDetail, formRow, toast) {
    const alreadyExists = formRowDetail.some((existingRow) => existingRow.branch_id === formRow.branch_id);
    if (alreadyExists) {
        toast.add({
            severity: 'warn',
            summary: 'Already Exist',
            detail: 'Branch already exist',
            life: 3000
        });
        return true;
    }

    return false;
}
export function checkEditProductAlreadyExist(formRowDetail, editingRow, toast, index) {
    const alreadyExists = formRowDetail.some((existingRow, i) => {
        return i !== index && existingRow.product_id === editingRow.product_id;
    });

    if (alreadyExists) {
        toast.add({
            severity: 'warn',
            summary: 'Already Exist',
            detail: 'Product already exist',
            life: 3000
        });
        return true;
    }

    return false;
}
export function checkEditAccountAlreadyExist(formRowDetail, editingRow, toast, index) {
    const alreadyExists = formRowDetail.some((existingRow, i) => {
        return i !== index && existingRow.account_id === editingRow.account_id;
    });

    if (alreadyExists) {
        toast.add({
            severity: 'warn',
            summary: 'Already Exist',
            detail: 'Account already exist',
            life: 3000
        });
        return true;
    }

    return false;
}
export function checkEditPartyAlreadyExist(formRowDetail, editingRow, toast, index) {
    const alreadyExists = formRowDetail.some((existingRow, i) => {
        return i !== index && existingRow.party_id === editingRow.party_id;
    });

    if (alreadyExists) {
        toast.add({
            severity: 'warn',
            summary: 'Already Exist',
            detail: 'Party already exist',
            life: 3000
        });
        return true;
    }

    return false;
}
export function checkEditBranchAlreadyExist(formRowDetail, editingRow, toast, index) {
    const alreadyExists = formRowDetail.some((existingRow, i) => {
        return i !== index && existingRow.branch_id === editingRow.branch_id;
    });

    if (alreadyExists) {
        toast.add({
            severity: 'warn',
            summary: 'Already Exist',
            detail: 'Branch already exist',
            life: 3000
        });
        return true;
    }

    return false;
}

export function addRowValidation({ formRowDetail, formRow, rowRef, toast }) {
    const error = [];
    let firstInvalidInput = null;
    let rowStart = null;

    if (!rowRef.value) return false;

    const inputs = rowRef.value.querySelectorAll('[data-required], [data-only-numbers], [greater-than-zero], [greater-or-equal-than-zero], [if-greater-than-zero]');

    inputs.forEach((input) => {
        const name = input.getAttribute('name') || input.querySelector('[name]')?.getAttribute('name');
        const value = formRow[name];

        if (!rowStart && input.hasAttribute('row-start-focus')) {
            const rowStartFocus = input.getAttribute('row-start-focus');
            rowStart = document.querySelector(`[data-focus-index="${rowStartFocus}"]`);
        }

        if (input.hasAttribute('data-required') && (value === '' || value == null)) {
            input.classList.add('p-invalid');
            if (!firstInvalidInput) firstInvalidInput = input;
            error.push({ name, error: 'This field is required' });
        }

        if (input.hasAttribute('data-only-numbers')) {
            if (isNaN(removeCommas(value))) {
                input.classList.add('p-invalid');
                if (!firstInvalidInput) firstInvalidInput = input;
                error.push({ name, error: 'Only numbers are allowed' });
            }
            if (value == '') {
                input.classList.add('p-invalid');
                if (!firstInvalidInput) firstInvalidInput = input;
                error.push({
                    name,
                    error: 'must be greater than or equal to 0'
                });
            }
        }

        if (input.hasAttribute('greater-than-zero') && Number(removeCommas(value)) <= 0) {
            input.classList.add('p-invalid');
            if (!firstInvalidInput) firstInvalidInput = input;
            error.push({ name, error: 'Must be greater than 0' });
        }

        if (input.hasAttribute('greater-or-equal-than-zero') && Number(removeCommas(value)) < 0) {
            input.classList.add('p-invalid');
            if (!firstInvalidInput) firstInvalidInput = input;
            error.push({ name, error: 'Must be greater or equal to 0' });
        }
        if (input.hasAttribute('if-greater-than-zero')) {
            const otherField = input.getAttribute('if-greater-than-zero');
            const otherValue = formRow[otherField];

            const currentVal = Number(removeCommas(value)) || 0;
            const otherVal = Number(removeCommas(otherValue)) || 0;

            if (currentVal > 0 && otherVal > 0) {
                input.classList.add('p-invalid');
                if (!firstInvalidInput) firstInvalidInput = input;
                error.push({
                    name,
                    error: `If ${name} > 0, then ${otherField} must be 0`
                });
            }
            if (currentVal < 0) {
                input.classList.add('p-invalid');
                if (!firstInvalidInput) firstInvalidInput = input;
                error.push({
                    name,
                    error: `${name} must be greater than or equal to 0`
                });
            }
            if (value == '') {
                input.classList.add('p-invalid');
                if (!firstInvalidInput) firstInvalidInput = input;
                error.push({ name, error: 'Only numbers are allowed' });
            }
        }
    });

    if (error.length > 0) {
        toast.add({
            severity: 'error',
            summary: 'Validation Error',
            detail: error.map((e) => `${e.name}: ${e.error}`).join('\n'),
            life: 6000
        });

        if (firstInvalidInput) {
            setCurrentActiveEl(firstInvalidInput);
            firstInvalidInput.focus();
            firstInvalidInput.click();
            setTimeout(() => {
                const searchInput = firstInvalidInput.querySelector('input[role="searchbox"]') || document.querySelector('input[role="searchbox"]');
                if (searchInput) {
                    searchInput.focus();
                    searchInput.select();
                }
            }, 100);
        }

        return false;
    }

    formRowDetail.value.push({ ...formRow });

    if (rowStart) {
        setCurrentActiveEl(rowStart);
        rowStart.focus();
        rowStart.click();
        setTimeout(() => {
            const searchInput = rowStart.querySelector('input[role="searchbox"]') || document.querySelector('input[role="searchbox"]');
            if (searchInput) {
                searchInput.focus();
                searchInput.select();
            }
        }, 100);
    }
    return true;
}

export function editRowValidation({ editRowRef, editingRow, toast }) {
    const error = [];
    const inputs = editRowRef.querySelectorAll('[data-required], [data-only-numbers], [greater-than-zero], [greater-or-equal-than-zero], [if-greater-than-zero]');

    inputs.forEach((input) => {
        const name = input.getAttribute('name') || input.querySelector('[name]')?.getAttribute('name');
        const value = editingRow?.[name];

        input.classList.remove('p-invalid');

        if (input.hasAttribute('data-required') && (value === '' || value == null)) {
            input.classList.add('p-invalid');
            error.push({ name, error: 'This field is required' });
        }

        if (input.hasAttribute('data-only-numbers') && isNaN(removeCommas(value))) {
            input.classList.add('p-invalid');
            error.push({ name, error: 'Only numbers are allowed' });
        }

        if (input.hasAttribute('greater-than-zero') && Number(removeCommas(value)) <= 0) {
            input.classList.add('p-invalid');
            error.push({ name, error: 'Must be greater than 0' });
        }

        if (input.hasAttribute('greater-or-equal-than-zero') && Number(removeCommas(value)) < 0) {
            input.classList.add('p-invalid');
            error.push({ name, error: 'Must be greater or equal to 0' });
        }
        if (input.hasAttribute('if-greater-than-zero')) {
            const otherField = input.getAttribute('if-greater-than-zero');
            const otherValue = editingRow[otherField];

            const currentVal = Number(removeCommas(value)) || 0;
            const otherVal = Number(removeCommas(otherValue)) || 0;

            if (currentVal > 0 && otherVal > 0) {
                input.classList.add('p-invalid');
                error.push({
                    name,
                    error: `If ${name} > 0, then ${otherField} must be 0`
                });
            }
            if (currentVal < 0) {
                input.classList.add('p-invalid');
                error.push({
                    name,
                    error: `${name} must be greater than or equal to 0`
                });
            }
            if (value == '') {
                input.classList.add('p-invalid');
                error.push({ name, error: 'Only numbers are allowed' });
            }
        }
    });

    if (error.length > 0) {
        toast.add({
            severity: 'error',
            summary: 'Validation Error',
            detail: error.map((e) => `${e.name}: ${e.error}`).join('\n'),
            life: 5000
        });

        return false;
    }
    return true;
}

export function addRowValidationSecondary({ formRowDetailSecondary, formRowSecondary, rowRefSecondary, toast }) {
    const error = [];
    let firstInvalidInput = null;
    let rowStart = null;

    if (!rowRefSecondary.value) return false;

    const inputs = rowRefSecondary.value.querySelectorAll('[data-required], [data-only-numbers], [greater-than-zero], [greater-or-equal-than-zero], [if-greater-than-zero]');

    inputs.forEach((input) => {
        const name = input.getAttribute('name') || input.querySelector('[name]')?.getAttribute('name');
        const value = formRowSecondary[name];

        if (!rowStart && input.hasAttribute('row-start-focus')) {
            const rowStartFocus = input.getAttribute('row-start-focus');
            rowStart = document.querySelector(`[data-focus-index="${rowStartFocus}"]`);
        }

        if (input.hasAttribute('data-required') && (value === '' || value == null)) {
            input.classList.add('p-invalid');
            if (!firstInvalidInput) firstInvalidInput = input;
            error.push({ name, error: 'This field is required' });
        }

        if (input.hasAttribute('data-only-numbers')) {
            if (isNaN(removeCommas(value))) {
                input.classList.add('p-invalid');
                if (!firstInvalidInput) firstInvalidInput = input;
                error.push({ name, error: 'Only numbers are allowed' });
            }
            if (value == '') {
                input.classList.add('p-invalid');
                if (!firstInvalidInput) firstInvalidInput = input;
                error.push({
                    name,
                    error: 'must be greater than or equal to 0'
                });
            }
        }

        if (input.hasAttribute('greater-than-zero') && Number(removeCommas(value)) <= 0) {
            input.classList.add('p-invalid');
            if (!firstInvalidInput) firstInvalidInput = input;
            error.push({ name, error: 'Must be greater than 0' });
        }

        if (input.hasAttribute('greater-or-equal-than-zero') && Number(removeCommas(value)) < 0) {
            input.classList.add('p-invalid');
            if (!firstInvalidInput) firstInvalidInput = input;
            error.push({ name, error: 'Must be greater or equal to 0' });
        }
        if (input.hasAttribute('if-greater-than-zero')) {
            const otherField = input.getAttribute('if-greater-than-zero');
            const otherValue = formRowSecondary[otherField];

            const currentVal = Number(removeCommas(value)) || 0;
            const otherVal = Number(removeCommas(otherValue)) || 0;

            if (currentVal > 0 && otherVal > 0) {
                input.classList.add('p-invalid');
                if (!firstInvalidInput) firstInvalidInput = input;
                error.push({
                    name,
                    error: `If ${name} > 0, then ${otherField} must be 0`
                });
            }
            if (currentVal < 0) {
                input.classList.add('p-invalid');
                if (!firstInvalidInput) firstInvalidInput = input;
                error.push({
                    name,
                    error: `${name} must be greater than or equal to 0`
                });
            }
            if (value == '') {
                input.classList.add('p-invalid');
                if (!firstInvalidInput) firstInvalidInput = input;
                error.push({ name, error: 'Only numbers are allowed' });
            }
        }
    });

    if (error.length > 0) {
        toast.add({
            severity: 'error',
            summary: 'Validation Error',
            detail: error.map((e) => `${e.name}: ${e.error}`).join('\n'),
            life: 6000
        });

        if (firstInvalidInput) {
            setCurrentActiveEl(firstInvalidInput);
            firstInvalidInput.focus();
            firstInvalidInput.click();
            setTimeout(() => {
                const searchInput = firstInvalidInput.querySelector('input[role="searchbox"]') || document.querySelector('input[role="searchbox"]');
                if (searchInput) {
                    searchInput.focus();
                    searchInput.select();
                }
            }, 100);
        }

        return false;
    }

    formRowDetailSecondary.value.push({ ...formRowSecondary });

    if (rowStart) {
        setCurrentActiveEl(rowStart);
        rowStart.focus();
        rowStart.click();
        setTimeout(() => {
            const searchInput = rowStart.querySelector('input[role="searchbox"]') || document.querySelector('input[role="searchbox"]');
            if (searchInput) {
                searchInput.focus();
                searchInput.select();
            }
        }, 100);
    }
    return true;
}

export function editRowValidationSecondary({ editRowRefSecondary, editingRowSecondary, toast }) {
    const error = [];
    const inputs = editRowRefSecondary.querySelectorAll('[data-required], [data-only-numbers], [greater-than-zero], [greater-or-equal-than-zero], [if-greater-than-zero]');

    inputs.forEach((input) => {
        const name = input.getAttribute('name') || input.querySelector('[name]')?.getAttribute('name');
        const value = editingRowSecondary?.[name];

        input.classList.remove('p-invalid');

        if (input.hasAttribute('data-required') && (value === '' || value == null)) {
            input.classList.add('p-invalid');
            error.push({ name, error: 'This field is required' });
        }

        if (input.hasAttribute('data-only-numbers') && isNaN(removeCommas(value))) {
            input.classList.add('p-invalid');
            error.push({ name, error: 'Only numbers are allowed' });
        }

        if (input.hasAttribute('greater-than-zero') && Number(removeCommas(value)) <= 0) {
            input.classList.add('p-invalid');
            error.push({ name, error: 'Must be greater than 0' });
        }

        if (input.hasAttribute('greater-or-equal-than-zero') && Number(removeCommas(value)) < 0) {
            input.classList.add('p-invalid');
            error.push({ name, error: 'Must be greater or equal to 0' });
        }
        if (input.hasAttribute('if-greater-than-zero')) {
            const otherField = input.getAttribute('if-greater-than-zero');
            const otherValue = editingRowSecondary[otherField];

            const currentVal = Number(removeCommas(value)) || 0;
            const otherVal = Number(removeCommas(otherValue)) || 0;

            if (currentVal > 0 && otherVal > 0) {
                input.classList.add('p-invalid');
                error.push({
                    name,
                    error: `If ${name} > 0, then ${otherField} must be 0`
                });
            }
            if (currentVal < 0) {
                input.classList.add('p-invalid');
                error.push({
                    name,
                    error: `${name} must be greater than or equal to 0`
                });
            }
            if (value == '') {
                input.classList.add('p-invalid');
                error.push({ name, error: 'Only numbers are allowed' });
            }
        }
    });

    if (error.length > 0) {
        toast.add({
            severity: 'error',
            summary: 'Validation Error',
            detail: error.map((e) => `${e.name}: ${e.error}`).join('\n'),
            life: 5000
        });

        return false;
    }
    return true;
}

export function addRowValidationThird({ formRowDetailThird, formRowThird, rowRefThird, toast }) {
    const error = [];
    let firstInvalidInput = null;
    let rowStart = null;

    if (!rowRefThird.value) return false;

    const inputs = rowRefThird.value.querySelectorAll('[data-required], [data-only-numbers], [greater-than-zero], [greater-or-equal-than-zero], [if-greater-than-zero]');

    inputs.forEach((input) => {
        const name = input.getAttribute('name') || input.querySelector('[name]')?.getAttribute('name');
        const value = formRowThird[name];

        if (!rowStart && input.hasAttribute('row-start-focus')) {
            const rowStartFocus = input.getAttribute('row-start-focus');
            rowStart = document.querySelector(`[data-focus-index="${rowStartFocus}"]`);
        }

        if (input.hasAttribute('data-required') && (value === '' || value == null)) {
            input.classList.add('p-invalid');
            if (!firstInvalidInput) firstInvalidInput = input;
            error.push({ name, error: 'This field is required' });
        }

        if (input.hasAttribute('data-only-numbers')) {
            if (isNaN(removeCommas(value))) {
                input.classList.add('p-invalid');
                if (!firstInvalidInput) firstInvalidInput = input;
                error.push({ name, error: 'Only numbers are allowed' });
            }
            if (value == '') {
                input.classList.add('p-invalid');
                if (!firstInvalidInput) firstInvalidInput = input;
                error.push({
                    name,
                    error: 'must be greater than or equal to 0'
                });
            }
        }

        if (input.hasAttribute('greater-than-zero') && Number(removeCommas(value)) <= 0) {
            input.classList.add('p-invalid');
            if (!firstInvalidInput) firstInvalidInput = input;
            error.push({ name, error: 'Must be greater than 0' });
        }

        if (input.hasAttribute('greater-or-equal-than-zero') && Number(removeCommas(value)) < 0) {
            input.classList.add('p-invalid');
            if (!firstInvalidInput) firstInvalidInput = input;
            error.push({ name, error: 'Must be greater or equal to 0' });
        }
        if (input.hasAttribute('if-greater-than-zero')) {
            const otherField = input.getAttribute('if-greater-than-zero');
            const otherValue = formRowThird[otherField];

            const currentVal = Number(removeCommas(value)) || 0;
            const otherVal = Number(removeCommas(otherValue)) || 0;

            if (currentVal > 0 && otherVal > 0) {
                input.classList.add('p-invalid');
                if (!firstInvalidInput) firstInvalidInput = input;
                error.push({
                    name,
                    error: `If ${name} > 0, then ${otherField} must be 0`
                });
            }
            if (currentVal < 0) {
                input.classList.add('p-invalid');
                if (!firstInvalidInput) firstInvalidInput = input;
                error.push({
                    name,
                    error: `${name} must be greater than or equal to 0`
                });
            }
            if (value == '') {
                input.classList.add('p-invalid');
                if (!firstInvalidInput) firstInvalidInput = input;
                error.push({ name, error: 'Only numbers are allowed' });
            }
        }
    });

    if (error.length > 0) {
        toast.add({
            severity: 'error',
            summary: 'Validation Error',
            detail: error.map((e) => `${e.name}: ${e.error}`).join('\n'),
            life: 6000
        });

        if (firstInvalidInput) {
            setCurrentActiveEl(firstInvalidInput);
            firstInvalidInput.focus();
            firstInvalidInput.click();
            setTimeout(() => {
                const searchInput = firstInvalidInput.querySelector('input[role="searchbox"]') || document.querySelector('input[role="searchbox"]');
                if (searchInput) {
                    searchInput.focus();
                    searchInput.select();
                }
            }, 100);
        }

        return false;
    }

    formRowDetailThird.value.push({ ...formRowThird });

    if (rowStart) {
        setCurrentActiveEl(rowStart);
        rowStart.focus();
        rowStart.click();
        setTimeout(() => {
            const searchInput = rowStart.querySelector('input[role="searchbox"]') || document.querySelector('input[role="searchbox"]');
            if (searchInput) {
                searchInput.focus();
                searchInput.select();
            }
        }, 100);
    }
    return true;
}

export function editRowValidationThird({ editRowRefThird, editingRowThird, toast }) {
    const error = [];
    const inputs = editRowRefThird.querySelectorAll('[data-required], [data-only-numbers], [greater-than-zero], [greater-or-equal-than-zero], [if-greater-than-zero]');

    inputs.forEach((input) => {
        const name = input.getAttribute('name') || input.querySelector('[name]')?.getAttribute('name');
        const value = editingRowThird?.[name];

        input.classList.remove('p-invalid');

        if (input.hasAttribute('data-required') && (value === '' || value == null)) {
            input.classList.add('p-invalid');
            error.push({ name, error: 'This field is required' });
        }

        if (input.hasAttribute('data-only-numbers') && isNaN(removeCommas(value))) {
            input.classList.add('p-invalid');
            error.push({ name, error: 'Only numbers are allowed' });
        }

        if (input.hasAttribute('greater-than-zero') && Number(removeCommas(value)) <= 0) {
            input.classList.add('p-invalid');
            error.push({ name, error: 'Must be greater than 0' });
        }

        if (input.hasAttribute('greater-or-equal-than-zero') && Number(removeCommas(value)) < 0) {
            input.classList.add('p-invalid');
            error.push({ name, error: 'Must be greater or equal to 0' });
        }
        if (input.hasAttribute('if-greater-than-zero')) {
            const otherField = input.getAttribute('if-greater-than-zero');
            const otherValue = editingRowThird[otherField];

            const currentVal = Number(removeCommas(value)) || 0;
            const otherVal = Number(removeCommas(otherValue)) || 0;

            if (currentVal > 0 && otherVal > 0) {
                input.classList.add('p-invalid');
                error.push({
                    name,
                    error: `If ${name} > 0, then ${otherField} must be 0`
                });
            }
            if (currentVal < 0) {
                input.classList.add('p-invalid');
                error.push({
                    name,
                    error: `${name} must be greater than or equal to 0`
                });
            }
            if (value == '') {
                input.classList.add('p-invalid');
                error.push({ name, error: 'Only numbers are allowed' });
            }
        }
    });

    if (error.length > 0) {
        toast.add({
            severity: 'error',
            summary: 'Validation Error',
            detail: error.map((e) => `${e.name}: ${e.error}`).join('\n'),
            life: 5000
        });

        return false;
    }
    return true;
}

export const userMenuTransform = (userMenu) => {
    const flattenTo = (link) => (link.startsWith('/') ? link : '/' + link);

    const transformItems = (items, level = 1) => {
        return items.map((item) => {
            let icon = item.icon || (level === 2 ? 'pi pi-fw pi-bookmark' : level === 3 ? 'pi pi-file' : 'pi pi-fw pi-home');

            const transformed = {
                label: item.name,
                icon,
                ...(item.link ? { to: flattenTo(item.link) } : {})
            };

            if (item.children && item.children.length > 0) {
                transformed.items = transformItems(item.children, level + 1);
            }

            return transformed;
        });
    };
    const branchId = String(activeBranchId.value); // make sure it's a string
    const menuArray = Array.isArray(userMenu) ? userMenu : userMenu[branchId] || [];

    return [
        {
            label: 'Main Menu',
            items: transformItems(menuArray, 1)
        }
    ];
};

export const activeBranchId = ref();

export const getBranchName = (branches, id) => {
    const branch_name = ref('');
    const get_branch_name = branches.find((branch) => branch.id === id);
    if (get_branch_name) {
        branch_name.value = get_branch_name.name;
    }
    return branch_name.value;
};

export const activeAccountingPeriodId = ref();

export function getPermissionFlags(currentPath) {
    const activeBranchId = AuthService.getActiveBranchId();
    const permissions = AuthService.getPermission();
    const p = permissions.find((p) => p.link === currentPath && p.branch_id === activeBranchId);

    return {
        canAdd: p?.add_permission === '1' || false,
        canUpdate: p?.update_permission === '1' || false,
        canDelete: p?.delete_permission === '1' || false,
        canNavigation: p?.navigation_permission === '1' || false,
        canPrint: p?.print_permission === '1' || false,
        approval_stage: p?.approval_stage ?? 1
    };
}

export const exportPdfUrl = ref(null);
export const exportExcelUrl = ref(null);
export const exportExcelFileName = ref(null);
export const exportErrorMessage = ref(null);
export const getPrintByParams = async (print_format, print_type, voucher_type = null, voucher_id = null) => {
    loadingState.start();
    const payload = {
        print_format,
        print_type,
        voucher_type,
        voucher_id,
        user_id: AuthService.getUser()?.id || null,
        branch_id: AuthService.getActiveBranchId()
    };
    try {
        const response = await axios.post(API_RPT_URL, {
            payload
        });
        if (response.data.success !== true) {
            loadingState.stop();
            exportErrorMessage.value = '* An unexpected error occurred while generating the print. Please try again or contact support.\n\n' + response.data.message;
            return;
        }
        const fileUrl = response.data.public_url;
        const blob = await fetch(fileUrl).then((res) => res.blob());
        const url = window.URL.createObjectURL(blob);
        if (print_format === 'pdf') {
            exportPdfUrl.value = url;
        } else {
            exportExcelUrl.value = url;
            exportExcelFileName.value = response.data.file_name;
        }
    } catch (e) {
        exportErrorMessage.value = '* An unexpected error occurred while generating the print. Please try again or contact support.\n\n' + e.message;
    }
    loadingState.stop();
};

export const onDownloadExcelFile = () => {
    if (!exportExcelUrl.value) return;
    const link = document.createElement('a');
    link.href = exportExcelUrl.value;
    link.download = exportExcelFileName.value;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

export function getStartEndDateRangeDbFormat(dateRange) {
    if (!dateRange) return { start_date: null, end_date: null };

    const toYMD = (date) => {
        let d;
        if (date instanceof Date) {
            d = new Date(date);
        } else if (typeof date === 'string') {
            // handle "dd/mm/yyyy" format
            const [dd, mm, yyyy] = date.split('/');
            d = new Date(`${yyyy}-${mm}-${dd}`);
        } else {
            return null;
        }

        // Force local date string in YYYY-MM-DD
        return d.toLocaleDateString('en-CA');
    };

    // Array case [start, end]
    if (Array.isArray(dateRange)) {
        return {
            start_date: toYMD(dateRange[0]),
            end_date: toYMD(dateRange[1])
        };
    }

    // String case "dd/mm/yyyy - dd/mm/yyyy"
    if (typeof dateRange === 'string') {
        const [start, end] = dateRange.split(' - ');
        return {
            start_date: toYMD(start),
            end_date: toYMD(end)
        };
    }

    return { start_date: null, end_date: null };
}
export function convertCalenderDateToDbDate(date) {
    let d;
    if (date instanceof Date) {
        d = new Date(date);
    } else if (typeof date === 'string') {
        // handle "dd/mm/yyyy" format
        const [dd, mm, yyyy] = date.split('/');
        d = new Date(`${yyyy}-${mm}-${dd}`);
    } else {
        return null;
    }

    // Force local date string in YYYY-MM-DD
    return d.toLocaleDateString('en-CA');
}
export function getFirstDayOfDate(date) {
    const y = date.getFullYear();
    const m = date.getMonth() + 1;
    return `${y}-${String(m).padStart(2, '0')}-01`;
}

export function generateColors(count) {
    const baseColors = [
        '#6C63FF',
        '#00BFA6',
        '#FFB547',
        '#FF6B6B',
        '#7F8C8D',
        '#3498DB',
        '#34C38F',
        '#F1B44C',
        '#F46A6A',
        '#74788D',
        '#8E44AD',
        '#1ABC9C',
        '#E67E22',
        '#C0392B',
        '#16A085',
        '#2980B9',
        '#27AE60',
        '#F39C12',
        '#D35400',
        '#E74C3C',
        '#9B59B6',
        '#2ECC71',
        '#F1C40F',
        '#E67E22',
        '#4ECDC4',
        '#2C3E50',
        '#BDC3C7',
        '#95A5A6',
        '#D35400'
    ];

    return Array.from({ length: count }, (_, i) => baseColors[i % baseColors.length]);
}
